import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import AdminLayout from "@/components/admin/AdminLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, Download, Mail, Phone, Building2, Users } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface Contact {
  email: string;
  full_name: string;
  phone: string | null;
  company_name: string | null;
  devis_count: number;
  last_devis: string;
}

const AdminContacts = () => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [filtered, setFiltered] = useState<Contact[]>([]);
  const [search, setSearch] = useState("");
  const { toast } = useToast();

  useEffect(() => {
    fetchContacts();
  }, []);

  useEffect(() => {
    if (!search) {
      setFiltered(contacts);
      return;
    }
    const s = search.toLowerCase();
    setFiltered(
      contacts.filter(
        (c) =>
          c.full_name?.toLowerCase().includes(s) ||
          c.email?.toLowerCase().includes(s) ||
          c.company_name?.toLowerCase().includes(s)
      )
    );
  }, [contacts, search]);

  const fetchContacts = async () => {
    const { data } = await supabase
      .from("quote_requests")
      .select("*")
      .order("created_at", { ascending: false });

    if (!data) return;

    // Group by email to get unique contacts
    const contactMap = new Map<string, Contact>();
    data.forEach((d) => {
      const existing = contactMap.get(d.email);
      if (existing) {
        existing.devis_count++;
        // Keep most recent data
        if (d.created_at > existing.last_devis) {
          existing.full_name = d.full_name;
          existing.phone = d.phone || existing.phone;
          existing.company_name = d.company_name || existing.company_name;
          existing.last_devis = d.created_at;
        }
      } else {
        contactMap.set(d.email, {
          email: d.email,
          full_name: d.full_name,
          phone: d.phone,
          company_name: d.company_name,
          devis_count: 1,
          last_devis: d.created_at,
        });
      }
    });

    const contactList = Array.from(contactMap.values()).sort(
      (a, b) => new Date(b.last_devis).getTime() - new Date(a.last_devis).getTime()
    );
    setContacts(contactList);
    setFiltered(contactList);
  };

  const exportCSV = () => {
    const headers = ["Nom", "Email", "Téléphone", "Entreprise", "Nombre de devis", "Dernier devis"];
    const rows = filtered.map((c) => [
      c.full_name,
      c.email,
      c.phone || "",
      c.company_name || "",
      c.devis_count.toString(),
      new Date(c.last_devis).toLocaleDateString("fr-FR"),
    ]);

    const csvContent = [headers.join(";"), ...rows.map((r) => r.join(";"))].join("\n");
    const blob = new Blob(["\ufeff" + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `contacts-ams-${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);

    toast({ title: "Export réussi", description: `${filtered.length} contact(s) exporté(s)` });
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header with actions */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold">Contacts & Prospects</h2>
            <p className="text-sm text-muted-foreground">{contacts.length} contact(s) unique(s)</p>
          </div>
          <Button onClick={exportCSV} variant="outline" className="gap-2">
            <Download className="w-4 h-4" />
            Exporter CSV
          </Button>
        </div>

        {/* Search */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher par nom, email, entreprise..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
          </CardContent>
        </Card>

        {/* Contacts list */}
        <div className="grid gap-3">
          {filtered.map((contact) => (
            <Card key={contact.email} className="border-0 shadow-sm hover:shadow-md transition-shadow">
              <CardContent className="p-5">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="space-y-1">
                    <p className="font-semibold text-base">{contact.full_name}</p>
                    <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
                      <a href={`mailto:${contact.email}`} className="flex items-center gap-1 hover:text-primary transition-colors">
                        <Mail className="w-3.5 h-3.5" /> {contact.email}
                      </a>
                      {contact.phone && (
                        <a href={`tel:${contact.phone}`} className="flex items-center gap-1 hover:text-primary transition-colors">
                          <Phone className="w-3.5 h-3.5" /> {contact.phone}
                        </a>
                      )}
                      {contact.company_name && (
                        <span className="flex items-center gap-1">
                          <Building2 className="w-3.5 h-3.5" /> {contact.company_name}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <div className="text-center">
                      <p className="font-bold text-lg text-primary">{contact.devis_count}</p>
                      <p className="text-xs text-muted-foreground">devis</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground">Dernier devis</p>
                      <p className="text-sm">{new Date(contact.last_devis).toLocaleDateString("fr-FR")}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {filtered.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Aucun contact trouvé</p>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminContacts;
